import { useState, useEffect } from 'react';
import axios from 'axios';
import { Bell, BellOff } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
console.log('PushNotificationToggle API URL:', API);

// VAPID public key - must match the one in backend/.env
const VAPID_PUBLIC_KEY = 'BLRFuxyXlC8UMkM3m8mrTfUwHSfC5xQIJzpxmwTZc8jrp0GMAFcWjq4lJ_VOfDXROq2spnEnGnMx-H7nYJAQYUE';

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default function PushNotificationToggle() {
  const { language } = useLanguage();
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);

  useEffect(() => {
    let isMounted = true;
    
    const checkSubscription = async () => {
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        
        if (subscription) {
          // We have a local subscription - check if server has it too
          try {
            const response = await axios.get(`${API}/notifications/subscription-status`, {
              headers: getAuthHeaders()
            });
            if (isMounted) {
              if (!response.data.has_subscription) {
                // Server doesn't have it, clear local subscription
                console.log('Server subscription missing, clearing local');
                await subscription.unsubscribe();
                setIsSubscribed(false);
              } else {
                setIsSubscribed(true);
              }
            }
          } catch (error) {
            // Endpoint might not exist yet, fallback to local state
            if (isMounted) {
              setIsSubscribed(true);
            }
          }
        } else {
          if (isMounted) {
            setIsSubscribed(false);
          }
        }
      } catch (error) {
        console.error('Error checking subscription:', error);
      }
    };

    // Check if push notifications are supported
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    if (supported) {
      // Use a microtask to avoid synchronous setState in effect
      Promise.resolve().then(() => {
        if (isMounted) {
          setIsSupported(true);
        }
      });
      checkSubscription();
    }
    
    return () => {
      isMounted = false;
    };
  }, []);

  const subscribeToNotifications = async () => {
    setIsLoading(true);
    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        const msg = { fr: 'Permission de notification refusée', ru: 'Разрешение отклонено', md: 'Permisiune refuzată', uk: 'Дозвіл відхилено' };
        toast.error(msg[language] || msg.fr);
        setIsLoading(false);
        return;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      
      // Unsubscribe from any existing subscription first (in case of key change)
      const existingSub = await registration.pushManager.getSubscription();
      if (existingSub) {
        await existingSub.unsubscribe();
      }
      
      // Subscribe to push notifications with new key
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });

      console.log('Push subscription created:', subscription);

      // Send subscription to backend
      const subData = subscription.toJSON ? subscription.toJSON() : JSON.parse(JSON.stringify(subscription));
      const response = await axios.post(`${API}/notifications/subscribe`, {
        subscription: JSON.stringify(subData)
      }, {
        headers: getAuthHeaders()
      });
      
      console.log('Subscribe response:', response.data);

      if (response.data.success) {
        setIsSubscribed(true);
        const successMsg = { fr: 'Notifications activées !', ru: 'Уведомления включены!', md: 'Notificări activate!', uk: 'Сповіщення увімкнено!' };
        toast.success(successMsg[language] || successMsg.fr);
      } else {
        throw new Error('Server did not confirm subscription');
      }
    } catch (error) {
      console.error('Error subscribing:', error);
      console.error('Error details:', error.message, error.name);
      
      let errorMessage = '';
      
      // Check specific error types
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Notifications refusées. Autorisez-les dans les paramètres du navigateur.';
      } else if (error.name === 'NotSupportedError') {
        errorMessage = 'Notifications non supportées sur cet appareil.';
      } else if (error.message?.includes('permission')) {
        errorMessage = 'Permission refusée. Autorisez les notifications.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Erreur serveur. Réessayez plus tard.';
      } else if (!navigator.onLine) {
        errorMessage = 'Pas de connexion internet.';
      } else {
        errorMessage = 'Erreur lors de l\'activation. Réessayez.';
      }
      
      toast.error(errorMessage, { duration: 5000 });
      setIsSubscribed(false);
    }
    setIsLoading(false);
  };

  const testNotification = async () => {
    setIsTesting(true);
    
    // Get token
    const token = localStorage.getItem('token');
    
    if (!token) {
      toast.error('Session expirée. Reconnectez-vous.', { duration: 5000 });
      setIsTesting(false);
      return;
    }
    
    // Build URL with cache buster
    const url = `${API}/notifications/test?_t=${Date.now()}`;
    console.log('Testing notification, URL:', url);
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: '{}'
      });
      
      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);
      
      if (response.ok && data.success) {
        toast.success('Notification envoyée ! Vérifiez votre appareil.', { duration: 4000 });
      } else if (response.status === 400) {
        toast.error(data.detail || 'Activez d\'abord les notifications.', { duration: 5000 });
      } else if (response.status === 401) {
        toast.error('Session expirée. Reconnectez-vous.', { duration: 5000 });
      } else {
        toast.error(data.detail || data.message || 'Erreur lors du test', { duration: 5000 });
      }
    } catch (error) {
      console.error('Fetch error:', error.name, error.message);
      
      // Try to determine the cause
      if (!navigator.onLine) {
        toast.error('Pas de connexion internet.', { duration: 5000 });
      } else if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        // This usually means CORS issue or network error
        toast.error('Erreur de connexion. Rafraîchissez la page et réessayez.', { duration: 5000 });
      } else {
        toast.error(`Erreur: ${error.message}`, { duration: 5000 });
      }
    } finally {
      setIsTesting(false);
    }
  };

  const unsubscribeFromNotifications = async () => {
    setIsLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
        
        // Notify backend
        await axios.post(`${API}/notifications/unsubscribe`, {
          endpoint: subscription.endpoint
        }, {
          headers: getAuthHeaders()
        });
      }

      setIsSubscribed(false);
      const successMsg = { fr: 'Notifications désactivées', ru: 'Уведомления отключены', md: 'Notificări dezactivate', uk: 'Сповіщення вимкнено' };
      toast.success(successMsg[language] || successMsg.fr);
    } catch (error) {
      console.error('Error unsubscribing:', error);
      const errorMsg = { fr: 'Erreur lors de la désactivation', ru: 'Ошибка отключения', md: 'Eroare la dezactivare', uk: 'Помилка вимкнення' };
      toast.error(errorMsg[language] || errorMsg.fr);
    }
    setIsLoading(false);
  };

  if (!isSupported) {
    return (
      <div className="text-sm text-gray-500 italic">
        {({ fr: 'Notifications non supportées sur ce navigateur', ru: 'Уведомления не поддерживаются', md: 'Notificările nu sunt suportate', uk: 'Сповіщення не підтримуються' }[language] || 'Notifications non supportées sur ce navigateur')}
      </div>
    );
  }

  return (
    <div className="flex flex-col sm:flex-row gap-2">
      <button
        onClick={isSubscribed ? unsubscribeFromNotifications : subscribeToNotifications}
        disabled={isLoading}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
          isSubscribed 
            ? 'bg-green-100 text-green-700 hover:bg-green-200' 
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        } ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        data-testid="push-notification-toggle"
      >
        {isSubscribed ? (
          <>
            <Bell className="w-5 h-5" />
            <span className="text-sm font-medium">
              {({ fr: 'Notifications activées', ru: 'Уведомления вкл.', md: 'Notificări activate', uk: 'Сповіщення увімкнено' }[language] || 'Notifications activées')}
            </span>
          </>
        ) : (
          <>
            <BellOff className="w-5 h-5" />
            <span className="text-sm font-medium">
              {({ fr: 'Activer les notifications', ru: 'Включить уведомления', md: 'Activează notificări', uk: 'Увімкнути сповіщення' }[language] || 'Activer les notifications')}
            </span>
          </>
        )}
      </button>
      
      {/* Test button - always visible */}
      <button
        onClick={testNotification}
        disabled={isTesting}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
          isSubscribed 
            ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
            : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
        }`}
        data-testid="test-notification-btn"
      >
        {isTesting ? (
          <span className="text-sm font-medium">Envoi...</span>
        ) : (
          <span className="text-sm font-medium">
            {({ fr: 'Tester', ru: 'Тест', md: 'Test', uk: 'Тест' }[language] || 'Tester')}
          </span>
        )}
      </button>
    </div>
  );
}
